import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, CalendarDays, Coins, Sparkles, MapPin, Compass, Clock, CheckCircle2, X } from "lucide-react";

const Landing = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const target = user && typeof user === "object" ? "/plan" : "/register";

  const [selectedItinerary, setSelectedItinerary] = useState(null);

  const features = [
    { icon: CalendarDays, title: t.landing.feature_1_title, desc: t.landing.feature_1_desc },
    { icon: Coins, title: t.landing.feature_2_title, desc: t.landing.feature_2_desc },
    { icon: Sparkles, title: t.landing.feature_3_title, desc: t.landing.feature_3_desc },
  ];

  const examples = [
    {
      city: "Barcellona",
      duration: "3 Giorni",
      budget: "~350€ a persona",
      tag: "Budget Medio • Capodanno (30 Dic - 2 Gen)",
      description: "Panoramica del viaggio a Barcellona considerando i voli (11:30 arrivo - 18:30 rientro).",
      days: [
        {
          dayTitle: "GIORNO 1",
          daySubtitle: "Atterraggio alle 11:30, transfer e prima immersione",
          slots: [
            { time: "11:30 - 13:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Arrivo in aeroporto e Trasferimento in alloggio", desc: "Sbarco, ritiro bagagli e transfer verso l'alloggio per check-in e deposito valigie." },
            { time: "13:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~25 EUR", title: "Pranzo presso Cervecería 100 Montaditos", desc: "Primo assaggio dei sapori locali. Valutato 4.5/5 su TripAdvisor." },
            { time: "15:00 - 17:00", type: "activity", typeLabel: "Arte & Architettura", cost: "~10 EUR", title: "Passeggiata a Park Güell", desc: "Visita al famoso parco progettato da Gaudí, con splendide vedute e opere d'arte." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.6/5)", cost: "~40 EUR", title: "Cena presso La Paradeta", desc: "Ristorante di pesce fresco, grande varietà e atmosfera vivace. Valutato 4.6/5 su TripAdvisor." },
            { time: "21:30 - 23:30", type: "activity", typeLabel: "Movida & Spettacolo", cost: "~15 EUR", title: "Serata al Poble Espanyol", desc: "Visita a questo museo all'aperto per vivere l'atmosfera spagnola e godere di musica e divertimento." }
          ]
        },
        {
          dayTitle: "GIORNO 2",
          daySubtitle: "Immersione nella cultura e nella gastronomia",
          slots: [
            { time: "11:00 - 12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~35 EUR", title: "Brunch presso El Nacional", desc: "Rinomato per la sua selezione di tapas e piatti regionali. Valutato 4.4/5 su TripAdvisor." },
            { time: "13:00 - 15:00", type: "activity", typeLabel: "Arte & Cultura", cost: "~20 EUR", title: "Visita alla Sagrada Família", desc: "Visita all'iconica basilica di Gaudí, un capolavoro di architettura." },
            { time: "15:30 - 17:00", type: "activity", typeLabel: "Passeggiata", cost: "Gratis", title: "Passeggiata a La Rambla", desc: "Esplorazione della famosa strada pedonale con negozi e artisti di strada." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~30 EUR", title: "Cena presso Restaurante Taverna El Raval", desc: "Offre una raffinata cucina spagnola in un ambiente informale. Valutato 4.5/5 su TripAdvisor." },
            { time: "22:00 - 02:00", type: "activity", typeLabel: "Movida", cost: "~20 EUR", title: "Movida al Sala Apolo", desc: "Un club famoso nella scena musicale di Barcellona, offre concerti e dance party." }
          ]
        },
        {
          dayTitle: "GIORNO 3",
          daySubtitle: "Attività leggere e preparativi per il rientro",
          slots: [
            { time: "09:00 - 10:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~25 EUR", title: "Colazione presso Chök", desc: "Cafè specializzato in dolci e cioccolato. Valutato 4.4/5 su TripAdvisor." },
            { time: "11:00 - 12:30", type: "activity", typeLabel: "Mercato & Cultura", cost: "Gratis", title: "Visita al Mercato della Boqueria", desc: "Esplorazione di uno dei mercati più famosi di Barcellona, ideale per souvenir gastronomici." },
            { time: "13:00 - 15:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Check-out e Trasferimento verso l'aeroporto", desc: "Preparazione per il volo di ritorno e transfer all'aeroporto per il decollo delle 18:30." }
          ]
        }
      ]
    },
    {
      city: "Tokyo",
      duration: "8 Giorni",
      budget: "~1.200€ a persona",
      tag: "Grande Viaggio • Tradizione & Futuro",
      description: "Il mix perfetto tra i neon di Shinjuku, i templi di Asakusa e le escursioni a Nikko.",
      days: [
        {
          dayTitle: "GIORNO 1",
          daySubtitle: "Atterraggio alle 11:30, transfer e prima immersione",
          slots: [
            { time: "11:30 - 13:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Arrivo in aeroporto e Trasferimento in alloggio", desc: "Sbarco, ritiro bagagli e transfer verso l'alloggio per check-in e deposito valigie." },
            { time: "13:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~25 EUR", title: "Pranzo presso Ichiran Ramen", desc: "Primo assaggio dei ramen locali in un ambiente unico. Valutato 4.5/5 su TripAdvisor." },
            { time: "15:00 - 17:00", type: "activity", typeLabel: "Arte & Cultura", cost: "Gratis", title: "Visita al Tempio Senso-ji", desc: "Esplorazione del tempio più antico di Tokyo e passeggiata nel Nakamise-dori." },
            { time: "18:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~35 EUR", title: "Cena al ristorante Uobei Shibuya Dogenzaka", desc: "Prova il sushi al nastro trasportatore in un ristorante innovativo. Valutato 4.4/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 2",
          daySubtitle: "Giornata dedicata alla cultura e alle viste panoramiche",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Arte & Storia", cost: "Gratis", title: "Visita al Palazzo Imperiale", desc: "Esplora i giardini e i dintorni del Palazzo Imperiale." },
            { time: "12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~30 EUR", title: "Pranzo al ristorante Sushizanmai", desc: "Sushi freschissimo in un ristorante ben rinomato. Valutato 4.5/5 su TripAdvisor." },
            { time: "14:30 - 16:30", type: "activity", typeLabel: "Panorama", cost: "~20 EUR", title: "Punto panoramico dal Tokyo Skytree", desc: "Goditi la vista panoramica di Tokyo dall'osservatorio del Tokyo Skytree." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~45 EUR", title: "Cena presso Gonpachi Nishiazabu", desc: "Ristorante famoso per il suo ambiente unico e piatti della cucina giapponese. Valutato 4.4/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 3",
          daySubtitle: "Visita delle famose aree di Shibuya e Harajuku",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Iconico", cost: "Gratis", title: "Visita al quadrato di Shibuya", desc: "Esplora il famoso incrocio di Shibuya e le sue attrazioni." },
            { time: "12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~30 EUR", title: "Brunch presso The Great Burger", desc: "Un ottimo posto per hamburger e brunch. Valutato 4.5/5 su TripAdvisor." },
            { time: "14:00 - 17:00", type: "activity", typeLabel: "Shopping", cost: "Gratis", title: "Shopping a Harajuku", desc: "Passeggiata sulla Takeshita Street e esplorazione dei negozi." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.3/5)", cost: "~40 EUR", title: "Cena presso Izakaya Akasaka", desc: "Un izakaya tradizionale con piatti giapponesi autentici. Valutato 4.3/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 4",
          daySubtitle: "Immergiti nella cultura e nelle tradizioni giapponesi",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Cultura & Storia", cost: "Gratis", title: "Visita al Museo Edo-Tokyo", desc: "Scopri la storia di Tokyo dal periodo Edo fino a oggi." },
            { time: "12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~25 EUR", title: "Pranzo presso Tokyo Shoyu Ramen", desc: "Ramen deliziosi e autentici. Valutato 4.5/5 su TripAdvisor." },
            { time: "14:00 - 16:00", type: "activity", typeLabel: "Tradizione", cost: "~30 EUR", title: "Cerimonia del tè", desc: "Partecipa a una cerimonia tradizionale del tè giapponese." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~35 EUR", title: "Cena presso Kazu", desc: "Ristorante specializzato in piatti di pesce freschissimo. Valutato 4.4/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 5",
          daySubtitle: "Visita ai dintorni di Tokyo per una giornata nella natura",
          slots: [
            { time: "08:00 - 18:00", type: "activity", typeLabel: "Escursione UNESCO", cost: "~50 EUR", title: "Gita a Nikko", desc: "Esplora il patrimonio UNESCO di Nikko, con i suoi templi e la natura." },
            { time: "18:00 - 19:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~10 EUR", title: "Ritorno a Tokyo", desc: "Ritorno in città per la cena." },
            { time: "19:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~40 EUR", title: "Cena presso Tsukiji Outer Market", desc: "Gusta cibo fresco e piatti tipici. Valutato 4.5/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 6",
          daySubtitle: "Scopri il fascino pop e moderno di Tokyo",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Pop Culture", cost: "Gratis", title: "Visita di Akihabara", desc: "Centro della cultura otaku, dai negozi di anime ai caffè maid." },
            { time: "12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.6/5)", cost: "~35 EUR", title: "Pranzo presso Yuuhi", desc: "Ristorante noto per la cucina giapponese moderna. Valutato 4.6/5 su TripAdvisor." },
            { time: "14:00 - 17:00", type: "activity", typeLabel: "Futuristico", cost: "Gratis", title: "Visita a Odaiba", desc: "Scopri il quartiere futuristico di Odaiba e il famoso Gundam." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~30 EUR", title: "Cena presso Ramen Nagi", desc: "Ramen premiati con un grande assortimento di zuppe. Valutato 4.4/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 7",
          daySubtitle: "Giornata dedicata al relax e all'esperienza pratica",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Relax", cost: "Gratis", title: "Mattinata di relax al parco Yoyogi", desc: "Trascorri una mattinata tranquilla nel parco Yoyogi." },
            { time: "12:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~30 EUR", title: "Brunch presso Café de l'ambre", desc: "Caffè di alta qualità in un ambiente accogliente. Valutato 4.5/5 su TripAdvisor." },
            { time: "14:00 - 16:00", type: "activity", typeLabel: "Laboratorio", cost: "~50 EUR", title: "Laboratorio di ceramica (opzionale)", desc: "Partecipa a un laboratorio di ceramica giapponese." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~40 EUR", title: "Cena al ristorante Tempura Matsui", desc: "Assaggia tempura fresca in un ristorante rinomato. Valutato 4.5/5 su TripAdvisor." }
          ]
        },
        {
          dayTitle: "GIORNO 8",
          daySubtitle: "Ultime visite e ritorno a casa",
          slots: [
            { time: "10:00 - 12:00", type: "activity", typeLabel: "Shopping", cost: "Variabile", title: "Mattinata libera per shopping o visite", desc: "Ultima occasione per acquistare souvenir o visitare un'attrazione vicina." },
            { time: "12:00 - 15:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Check-out e Trasferimento verso l'aeroporto", desc: "Preparazione per il volo e trasferimento verso l'aeroporto." }
          ]
        }
      ]
    },
    {
      city: "Parigi",
      duration: "3 Giorni",
      budget: "~280€ a persona",
      tag: "Budget Medio • Arte & Gusto",
      description: "Panoramica del viaggio a Parigi considerando i voli (11:30 arrivo - 18:30 rientro).",
      days: [
        {
          dayTitle: "GIORNO 1",
          daySubtitle: "Atterraggio alle 11:30, transfer e prima immersione",
          slots: [
            { time: "11:30 - 13:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Arrivo in aeroporto e Trasferimento in alloggio", desc: "Sbarco, ritiro bagagli e transfer verso l'alloggio per check-in e deposito valigie." },
            { time: "13:30", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~30 EUR", title: "Pranzo presso Le Relais de l'Entrecôte", desc: "Primo assaggio dei sapori locali. Valutato 4.5/5 su TripAdvisor." },
            { time: "15:00 - 16:30", type: "activity", typeLabel: "Relax & Natura", cost: "Gratis", title: "Passeggiata presso il Giardino del Lussemburgo", desc: "Relax nel famoso giardino parigino, ideale per una passeggiata tranquilla." },
            { time: "17:00 - 18:30", type: "activity", typeLabel: "Arte & Architettura", cost: "Gratis", title: "Visita alla Cattedrale di Notre-Dame", desc: "Esplora la storica cattedrale e ammira la sua architettura gotica." },
            { time: "19:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.6/5)", cost: "~40 EUR", title: "Cena presso Le Petit Pontoise", desc: "Un'accogliente brasserie raffinata con piatti tradizionali francesi. Valutato 4.6/5 su TripAdvisor." },
            { time: "21:00", type: "activity", typeLabel: "Movida & Bar", cost: "~20 EUR", title: "Serata al bar Les Fous de la Reine", desc: "Un bar vivace e alla moda per divertirsi e gustare cocktail creativi." }
          ]
        },
        {
          dayTitle: "GIORNO 2",
          daySubtitle: "Un giorno dedicato all'arte, alla storia e alla gastronomia",
          slots: [
            { time: "11:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.4/5)", cost: "~35 EUR", title: "Brunch presso Café de Flore", desc: "Famoso café parigino, ideale per un brunch rilassato. Valutato 4.4/5 su TripAdvisor." },
            { time: "13:00 - 15:30", type: "activity", typeLabel: "Arte & Cultura", cost: "~17 EUR", title: "Visita al Museo del Louvre", desc: "Esplora il famoso museo, sede di opere d'arte iconiche." },
            { time: "16:00 - 17:30", type: "activity", typeLabel: "Passeggiata", cost: "Gratis", title: "Passeggiata lungo la Senna e Pont des Arts", desc: "Una piacevole passeggiata lungo il fiume per ammirare panorami e ponti storici." },
            { time: "18:00", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~40 EUR", title: "Cena presso Chez Janou", desc: "Un ristorante provenzale pittoresco con deliziosa cucina tradizionale. Valutato 4.5/5 su TripAdvisor." },
            { time: "21:00", type: "activity", typeLabel: "Spettacolo", cost: "~100 EUR", title: "Serata al Moulin Rouge", desc: "Spettacolo di cabaret iconico con performance straordinarie." }
          ]
        },
        {
          dayTitle: "GIORNO 3",
          daySubtitle: "Ultime esplorazioni e rientro",
          slots: [
            { time: "09:00 - 10:30", type: "activity", typeLabel: "Panorama", cost: "~26 EUR", title: "Visita alla Torre Eiffel", desc: "Salita sulla Torre Eiffel per panorami mozzafiato di Parigi." },
            { time: "10:45", type: "food", typeLabel: "Ristorazione consigliata (★ 4.5/5)", cost: "~25 EUR", title: "Colazione presso Angelina", desc: "Famosa per il suo cioccolato caldo e brioche. Valutato 4.5/5 su TripAdvisor." },
            { time: "11:30 - 13:00", type: "logistics", typeLabel: "Logistica & Transfer", cost: "~15 EUR", title: "Check-out e Trasferimento verso l'aeroporto", desc: "Ultimi momenti a Parigi e trasferimento all'aeroporto per il decollo delle 18:30." }
          ]
        }
      ]
    }
  ];

  return (
    <main className="relative z-10 overflow-hidden" data-testid="landing-page">
      {/* Sfondo con la Bussola Dorata */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-40 mix-blend-screen">
        <img
          src="/bg-compass.png"
          alt="Bussola Stellata"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 text-center flex flex-col items-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="w-8 h-[1px] bg-[#D4AF37]/60" />
          <span className="overline" data-testid="landing-overline">
            {t.landing.overline}
          </span>
          <span className="w-8 h-[1px] bg-[#D4AF37]/60" />
        </div>

        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-[#EDE7D8]">
          {t.landing.title.split(".")[0]}.
          <span className="block text-gradient-gold italic mt-2">
            {t.landing.title.split(".").slice(1).join(".").trim()}
          </span>
        </h1>

        <p className="mt-8 text-lg text-[#EDE7D8]/75 max-w-2xl leading-relaxed">
          {t.landing.subtitle}
        </p>

        {/* SEO Context Block aggiunto per posizionamento motori di ricerca */}
        <div className="mt-6 text-xs text-[#EDE7D8]/50 max-w-xl leading-relaxed">
          Il miglior pianificatore di viaggi online: genera itinerari personalizzati per mete come Barcellona, Tokyo e Parigi con comparazione voli e hotel in tempo reale.
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to={target} className="btn-pill" data-testid="landing-cta-primary">
            {t.landing.cta}
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto relative z-10" />

      {/* Vetrina Esempi Reali (Spostata subito in alto) */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-medium uppercase tracking-wider mb-4">
            <Compass className="w-3.5 h-3.5" /> 100% Gratuito • Accesso tramite Registrazione Email
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl text-[#EDE7D8]">
            Guarda cosa sa fare il Genio in Azione
          </h2>
          <p className="mt-4 text-[#EDE7D8]/75 text-base sm:text-lg">
            Ecco alcuni esempi reali di itinerari creati dal tool. Clicca su una card per leggerli subito in chiaro!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {examples.map((item, index) => (
            <div
              key={index}
              className="grid-card p-8 flex flex-col justify-between border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 transition-all duration-300 cursor-pointer group"
              onClick={() => setSelectedItinerary(item)}
            >
              <div>
                <span className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold px-3 py-1 rounded-full mb-4 border border-[#D4AF37]/20">
                  {item.tag}
                </span>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-2xl text-[#EDE7D8] group-hover:text-[#D4AF37] transition-colors">{item.city}</h3>
                  <span className="text-xs text-[#EDE7D8]/60 bg-black/30 px-2.5 py-1 rounded-md">{item.duration}</span>
                </div>
                <p className="text-[#D4AF37] font-medium text-sm mb-4">Budget: {item.budget}</p>
                <p className="text-sm text-[#EDE7D8]/70 leading-relaxed mb-6">{item.description}</p>
              </div>

              <div className="pt-4 border-t border-[#D4AF37]/10 flex items-center justify-between text-xs">
                <span className="text-[#EDE7D8]/50 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" /> Disponibile gratis
                </span>
                <span className="text-[#D4AF37] font-medium flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  Leggi itinerario <ArrowUpRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto relative z-10" />

      {/* Features grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {features.map((f, i) => (
            <div
              key={i}
              className={`grid-card p-8 fade-up ${
                i === 0 ? "md:col-span-5" : i === 1 ? "md:col-span-4" : "md:col-span-3"
              }`}
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`landing-feature-${i}`}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <f.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl mt-6 text-[#EDE7D8]">{f.title}</h3>
              <p className="text-sm text-[#EDE7D8]/70 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Modal con i dettagli completi */}
      {selectedItinerary && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6">
          <div className="bg-[#0b0e14] border border-[#D4AF37]/40 rounded-2xl max-w-3xl w-full p-5 sm:p-8 text-[#EDE7D8] relative shadow-2xl max-h-[92vh] flex flex-col">
            
            {/* Bottone Chiudi */}
            <button 
              onClick={() => setSelectedItinerary(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-2 z-10 bg-black/50 rounded-full border border-white/10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header del Modale */}
            <div className="mb-6 pr-10 border-b border-[#D4AF37]/20 pb-4">
              <span className="text-xs font-semibold text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1 rounded-full border border-[#D4AF37]/20 inline-block mb-3">
                ITINERARIO COORDINATO DAL GENIO
              </span>
              <h3 className="font-heading text-3xl sm:text-4xl text-[#EDE7D8] mb-1">Viaggio a {selectedItinerary.city}</h3>
              <p className="text-sm text-[#EDE7D8]/70 mb-3">{selectedItinerary.description}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-[#D4AF37]">
                <span className="bg-[#D4AF37]/10 px-3 py-1 rounded-md border border-[#D4AF37]/20 font-medium">
                  {selectedItinerary.duration}
                </span>
                <span className="bg-[#D4AF37]/10 px-3 py-1 rounded-md border border-[#D4AF37]/20 font-medium">
                  Budget stimato: {selectedItinerary.budget}
                </span>
              </div>
            </div>

            {/* Contenuto scrollabile */}
            <div className="space-y-6 overflow-y-auto pr-1 flex-1">
              <h4 className="font-heading text-xl text-[#EDE7D8]">Programma giorno per giorno</h4>

              {selectedItinerary.days.map((day, dIdx) => (
                <div key={dIdx} className="bg-[#121620] border border-[#D4AF37]/30 rounded-xl p-4 sm:p-5 space-y-4 shadow-lg">
                  
                  <div className="border-b border-[#D4AF37]/20 pb-3 flex items-center justify-between">
                    <span className="font-heading text-lg tracking-wide text-[#D4AF37] flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" /> {day.dayTitle}
                    </span>
                    <span className="text-xs text-slate-400 italic">{day.daySubtitle}</span>
                  </div>

                  <div className="space-y-3">
                    {day.slots.map((slot, sIdx) => (
                      <div key={sIdx} className="bg-black/40 border border-[#D4AF37]/20 rounded-lg p-3 sm:p-4 hover:border-[#D4AF37]/50 transition-colors">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded border border-[#D4AF37]/30 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {slot.time}
                            </span>
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                              {slot.typeLabel}
                            </span>
                          </div>
                          <span className="text-xs font-medium text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded border border-amber-500/20">
                            {slot.cost}
                          </span>
                        </div>

                        <h5 className="font-bold text-sm text-[#EDE7D8] mb-1">{slot.title}</h5>
                        <p className="text-xs text-slate-300 leading-relaxed">{slot.desc}</p>
                      </div>
                    ))}
                  </div>

                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-[#D4AF37]/20 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
              <span className="text-xs text-slate-400">✨ Servizio 100% gratuito (richiede registrazione email).</span>
              <Link 
                to={target} 
                onClick={() => setSelectedItinerary(null)}
                className="btn-pill w-full sm:w-auto text-center text-xs py-2 px-5"
              >
                Crea il tuo viaggio <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

          </div>
        </div>
      )}
    </main>
  );
};

export default Landing;
